import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * TrialRibbon — a compact "TRIAL · X DAYS LEFT" pill that lives in the header
 * whenever the user is currently on a Pro trial (via `effective_plan.source
 * === 'trial'`). Clicks straight through to /pricing.
 *
 * Visibility rules:
 *   * Hidden entirely for logged-out users, non-trial users, and users whose
 *     trial has already expired (backend already stops returning `trial`
 *     source once past `trial_end`).
 *   * Shrinks copy on the last day ("LAST DAY") for urgency.
 */
export default function TrialRibbon() {
  const { user } = useAuth();
  const [billing, setBilling] = useState(null);

  useEffect(() => {
    if (!user) { setBilling(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/billing/me");
        if (!cancelled) setBilling(r.data);
      } catch {
        // Silent — the ribbon is decorative, not blocking.
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  if (!user || !billing) return null;
  const eff = billing.effective || {};
  if (eff.source !== "trial" || !eff.trial_end) return null;

  const msLeft = new Date(eff.trial_end).getTime() - Date.now();
  const daysLeft = Math.max(0, Math.ceil(msLeft / (24 * 60 * 60 * 1000)));
  if (daysLeft <= 0) return null;

  const label = daysLeft === 1 ? "LAST DAY" : `${daysLeft} DAYS LEFT`;
  const urgent = daysLeft <= 2;

  return (
    <Link
      to="/pricing"
      data-testid="trial-ribbon"
      title="Upgrade to keep Pro after your trial ends"
      className={`shrink-0 hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10px] font-mono uppercase tracking-widest transition-colors ${
        urgent
          ? "border-[#c89f45] bg-[#c89f45]/15 text-[#c89f45] hover:bg-[#c89f45]/25"
          : "border-[#c89f45]/40 bg-transparent text-[#c89f45] hover:border-[#c89f45] hover:bg-[#c89f45]/10"
      }`}
    >
      <Sparkles size={11} className="shrink-0" />
      <span>PRO TRIAL · {label}</span>
    </Link>
  );
}
